import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import {
  Alert,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as programActions from "store/actions/input/program/programActions";
import * as baseLayerBurnStore from "store/baseLayerBurn";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const ENGINE = "united_es_loss_v1";
const FONT_SIZE = "0.875rem";

const RULES = [
  "Only use when 3 complete years of loss experience are available (exclude the current policy year).",
  "Credibility factor must be 0.07 or more to qualify for experience rating.",
  "Indemnity capped at $100,000 for any single claim. Total incurred (indemnity + ALAE) capped at the Maximum Single Loss for any single claim.",
];

// Matching the grey section bars used across the pricing tab.
const SectionBar = ({ label, info }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    spacing={0.75}
    sx={{ backgroundColor: "grey.100", px: 2, py: 1.25, borderRadius: 1 }}
  >
    <Typography fontSize={FONT_SIZE} fontWeight={700} color={"text.secondary"}>
      {label}
    </Typography>
    {info && (
      <Tooltip
        title={
          <Stack spacing={0.5} sx={{ py: 0.5 }}>
            {info.map((line) => (
              <Typography key={line} variant={"caption"}>
                {`• ${line}`}
              </Typography>
            ))}
          </Stack>
        }
        arrow
      >
        <InfoIcon sx={{ fontSize: 16, color: "text.disabled" }} />
      </Tooltip>
    )}
  </Stack>
);

const Row = ({ label, value, muted }) => (
  <TableRow>
    <TableCell
      sx={{
        fontSize: FONT_SIZE,
        color: "text.secondary",
        border: 0,
        py: 0.5,
        pl: 0,
      }}
    >
      {label}
    </TableCell>
    <TableCell
      align={"right"}
      sx={{
        fontSize: FONT_SIZE,
        fontWeight: 600,
        color: muted ? "text.secondary" : "text.primary",
        border: 0,
        py: 0.5,
        pr: 0,
      }}
    >
      {value}
    </TableCell>
  </TableRow>
);

const ExperienceRating = () => {
  const dispatch = useDispatch();
  const data = useSelector(programSelectors.getExperienceRating);
  const result = useSelector(programSelectors.getExperienceRatingResult);
  const baseLayerBurn = useSelector(baseLayerBurnStore.selectBaseLayerBurn);
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );

  const [error, setError] = useState(null);

  const [
    _triggerPricing,
    { isFetching },
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  // Basic-limits premium sums come from the GL engine result (option a).
  const glTotals = baseLayerBurn?.pricing?.values?.totals;
  const premBasic = glTotals?.prem_ops_basic_limits ?? null;
  const pcoBasic = glTotals?.pco_basic_limits ?? null;
  const hasGlResult = premBasic != null || pcoBasic != null;

  const handleUpdate = (field, value) =>
    dispatch(programActions.updateExperienceRating(field, value));

  const handleCalculate = async () => {
    setError(null);
    try {
      const response = await _triggerPricing({
        engine: ENGINE,
        metric: "program_pricing",
        // Only send the experience rating block — exclude lossRating /
        // autoLossRating so the engine doesn't try (and fail) to compute
        // those blocks alongside the experience rating.
        submission: {
          ...submission,
          lossRating: undefined,
          autoLossRating: undefined,
          experienceRating: {
            premBasicLimitsPremium: premBasic ?? 0,
            pcoBasicLimitsPremium: pcoBasic ?? 0,
            totalCappedLosses: data?.totalCappedLosses ?? null,
            applyExperienceMod: data?.applyExperienceMod ?? false,
          },
        },
      });
      if (response?.data?.failure) {
        setError(response.data.failure);
        dispatch(programActions.setExperienceRatingResult(null));
      } else {
        dispatch(
          programActions.setExperienceRatingResult(
            response?.data?.values?.experience_rating ?? null
          )
        );
      }
    } catch (err) {
      setError(err?.message ?? "Pricing request failed.");
      dispatch(programActions.setExperienceRatingResult(null));
    }
  };

  const mod = result?.experience_mod;

  return (
    <Stack direction={"column"} spacing={1.5} sx={{ pr: 1, pt: 1 }}>
      {/* ---------- Inputs ---------- */}
      <SectionBar label={"Rating Parameters"} info={RULES} />
      <Stack spacing={1.25} sx={{ px: 1, pt: 0.5 }}>
        <Stack direction={"row"} spacing={3}>
          <Typography variant={"caption"} color={"text.secondary"}>
            {"Basic-limits premium (from GL primary rating)"}
          </Typography>
        </Stack>
        <Stack direction={"row"} spacing={2}>
          <PrettyNumberTextField
            label={"Prem/Ops Basic Limits ($)"}
            variant={"filled"}
            size={"small"}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { textAlign: "right" } }}
            value={premBasic ?? null}
          />
          <PrettyNumberTextField
            label={"Products/PCO Basic Limits ($)"}
            variant={"filled"}
            size={"small"}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
            inputProps={{ style: { textAlign: "right" } }}
            value={pcoBasic ?? null}
          />
        </Stack>

        {!hasGlResult && (
          <Alert severity={"info"} sx={{ py: 0 }}>
            {
              "Run GL Pricing first — the experience rating uses the basic-limits premiums from the GL primary rating."
            }
          </Alert>
        )}

        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <Disabled ifReadOnly>
            <PrettyNumberTextField
              label={"Total Capped Losses in Experience Period ($)"}
              variant={"filled"}
              size={"small"}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { textAlign: "right" }, min: 0 }}
              value={data?.totalCappedLosses ?? null}
              onChangeNumber={(v) => handleUpdate("totalCappedLosses", v)}
            />
          </Disabled>
          <Disabled ifReadOnly>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(data?.applyExperienceMod)}
                  onChange={(e) =>
                    handleUpdate("applyExperienceMod", e.target.checked)
                  }
                />
              }
              label={"Apply Experience Mod"}
              sx={{
                whiteSpace: "nowrap",
                m: 0,
                "& .MuiTypography-root": { fontSize: FONT_SIZE },
              }}
            />
          </Disabled>
        </Stack>

        <Stack direction={"row"} justifyContent={"flex-end"}>
          <Button onClick={handleCalculate} isLoading={isFetching}>
            {"Calculate"}
          </Button>
        </Stack>

        {error && <Alert severity={"error"}>{error}</Alert>}
      </Stack>

      {/* ---------- Results ---------- */}
      {result && (
        <>
          <SectionBar label={"Experience Rating Summary"} />
          <TableContainer sx={{ px: 1 }}>
            <Table size={"small"} sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableBody>
                <Row
                  label={"Company Subject Loss Cost"}
                  value={numbers.currencyFormat(result.subject_loss_cost, 0)}
                />
                <Row
                  label={"Credibility Factor"}
                  value={
                    result.credibility_factor != null
                      ? numbers.dpString(3)(result.credibility_factor)
                      : "—"
                  }
                />
                <Row
                  label={"Expected Experience Ratio"}
                  value={
                    result.expected_experience_ratio != null
                      ? numbers.dpString(3)(result.expected_experience_ratio)
                      : "—"
                  }
                />
                <Row
                  label={"Maximum Single Loss"}
                  value={numbers.currencyFormat(result.max_single_loss, 0)}
                />
                <Row
                  label={"Total Capped Losses"}
                  value={numbers.currencyFormat(result.total_capped_losses, 0)}
                  muted
                />
                <Row
                  label={"Ultimate Loss Adjustment"}
                  value={numbers.currencyFormat(
                    result.ultimate_loss_adjustment,
                    0
                  )}
                  muted
                />
                <Row
                  label={"Actual Experience Ratio"}
                  value={
                    result.actual_experience_ratio != null
                      ? numbers.percentage(2)(result.actual_experience_ratio)
                      : "—"
                  }
                />
                <TableRow>
                  <TableCell colSpan={2} sx={{ border: 0, py: 0.5 }}>
                    <Divider />
                  </TableCell>
                </TableRow>
                <Row
                  label={"Experience Mod"}
                  value={mod != null ? numbers.percentage(1)(mod) : "—"}
                  bold
                />
              </TableBody>
            </Table>

            {result.credibility_factor != null && !result.qualifies && (
              <Alert severity={"warning"} sx={{ mt: 1 }}>
                {
                  "Credibility factor is below 0.07 — the risk does not qualify for experience rating."
                }
              </Alert>
            )}
          </TableContainer>
        </>
      )}
    </Stack>
  );
};

export default ExperienceRating;
